import { FC, MouseEventHandler, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    Column,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "./GlobalSearchFilter";
import { ColumnDef } from "@tanstack/table-core";
import { TNullable } from "../../common/types/common";
import { InputProps } from "react-select";

interface FilterProps {
    column: Column<any, any>,
}

// Column Filter
const Filter = ({
  column
}: FilterProps) => {
  const columnFilterValue = column.getFilterValue() as string;

  return (
    <>
      <DebouncedInput
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

type TEmptyFn = (..._params: any) => void;

interface DebouncedInputProps extends Partial<Omit<InputProps, 'onChange'>> {
    value?: string;
    onChange?: (_str: string) => void;
    debounce?: number;
}

// Global Filter
const DebouncedInput: FC<DebouncedInputProps> = ({
  value: initialValue = '',
  onChange = undefined,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState<string>(initialValue ?? '');

  useEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
        if (onChange) {
            onChange(value);
        }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </>
  );
};

interface TableContainerProps {
    columns: ColumnDef<any, any>[];
    data?: any[];
    tableClass?: string;
    theadClass?: string;
    divClassName?: string;
    isBordered?: boolean;
    isPagination?: boolean;
    isGlobalFilter?: boolean;
    paginationWrapper?: string;
    SearchPlaceholder?: string;
    pagination?: string;
    buttonClass?: string;
    buttonName?: string;
    isAddButton?: boolean;
    isCustomPageSize?: boolean;
    handleUserClick?: MouseEventHandler<HTMLButtonElement>;
    isJobListGlobalFilter?: boolean,

    manualPagination?: boolean;
    onPaginationChange?: TNullable<TEmptyFn>;
    paginationState?: TNullable<any>;
    pageCount?: number,

    manualSorting?: boolean;
    sortingState?: TNullable<any[]>;
    onSortingChange?: TNullable<TEmptyFn>;

    manualGlobalFilter?: boolean;
    globalFilterState?: string;
    onGlobalFilterChange?: (_searchTerm: string) => void;

    onRowClick?: (rowData: any) => void;
}

const TableContainer = ({
  columns = [],
  data = [],
  tableClass = '',
  theadClass = '',
  divClassName = '',
  isBordered = false,
  isPagination = false,
  isGlobalFilter = false,
  paginationWrapper = '',
  SearchPlaceholder = '',
  pagination = '',
  buttonClass = '',
  buttonName = '',
  isAddButton = false,
  isCustomPageSize = false,
  handleUserClick = undefined,
  isJobListGlobalFilter = false,

  manualPagination = false,
  onPaginationChange = null,
  paginationState = null,
  pageCount = undefined,

  manualSorting = false,
  sortingState = null,
  onSortingChange = null,

  manualGlobalFilter = false,
  globalFilterState = '',
  onGlobalFilterChange = undefined,

  onRowClick = undefined,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const tableSettings: any = {
      columns,
      data,
      filterFns: {
          fuzzy: fuzzyFilter,
      },
      state: {
          columnFilters,
          globalFilter,
      },
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: fuzzyFilter,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
  };

  if (manualPagination) {
      tableSettings.manualPagination = manualPagination;
      tableSettings.pageCount = pageCount;
      tableSettings.state.pagination = paginationState;
      tableSettings.onPaginationChange = onPaginationChange;
  }

  if (manualSorting) {
      tableSettings.onSortingChange = onSortingChange;
      tableSettings.state.sorting = sortingState;
  }

  if (manualGlobalFilter) {
      tableSettings.onGlobalFilterChange = onGlobalFilterChange;
      tableSettings.state.globalFilter = globalFilterState;
  }

  const table = useReactTable(tableSettings);

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
    options,
  } = table;

  const currentPage = getState().pagination.pageIndex;
  const pages = getPageOptions().slice(Math.max(currentPage - 1, 0), Math.max(currentPage - 1, 0) + 3);
  const lastPage = getPageOptions()[getPageOptions().length - 1];
  const hasPreLastPage = pages.includes(getPageOptions()[getPageOptions().length - 2]);
  const hasLastPages = pages.includes(lastPage);

  useEffect(() => {
     if (Number(isCustomPageSize)) {
         setPageSize(Number(20));
     }
  }, [isCustomPageSize, setPageSize]);

  return (
    <>

      <Row className="mb-2 d-flex flex-row justify-content-between">
        {isGlobalFilter && <DebouncedInput
            value={getState().globalFilter ?? ''}
            onChange={value => { if (options.onGlobalFilterChange) { options.onGlobalFilterChange(String(value)); } }}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
        />}

        {isJobListGlobalFilter && options.onGlobalFilterChange && <JobListGlobalFilter setGlobalFilter={options.onGlobalFilterChange} />}

        <Col sm={6} className="d-flex justify-content-end flex-row align-content-center">
          {isCustomPageSize && (
              <Col sm={6} className="mb-2">
                <select
                    className="form-select pageSize"
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                >
                  {[10, 20, 30, 40, 50].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                  ))}
                </select>
              </Col>
          )}

          {isAddButton && (
              <div className="text-sm-end ms-4">
                <Button type="button" className={buttonClass} onClick={handleUserClick}>
                  <i className="mdi mdi-plus me-1"></i> {buttonName}</Button>
              </div>
          )}
        </Col>

      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isSorted = header.column.getIsSorted();

                  return (
                    <th key={header.id} colSpan={header.colSpan} className={`${header.column.columnDef.enableSorting ? "sorting sorting_desc" : ""}`}>
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            {...{
                              style: header.column.getCanSort()
                                ? {cursor: 'pointer', 'userSelect': 'none'}
                                : {},
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {
                                isSorted
                                  ? {
                                        asc: (<span className="text-muted"> &uarr;</span>),
                                        desc: (<span className="text-muted"> &darr;</span>),
                                    }[isSorted]
                                  : (
                                        header.column.columnDef.enableSorting ? <span className="text-muted"> &uarr;&darr;</span> : null
                                    )
                            }
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} />
                            </div>
                          ) : null}
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map(row => {
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  style={{ cursor: onRowClick ? 'pointer' : undefined }}
                >
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>


      {
        isPagination && (
          <Row>
            <Col sm={12} md={5}>
              <div className="dataTables_info">{data.length} Results</div>
            </Col>
            <Col sm={12} md={7}>
              <div className={paginationWrapper}>
                <ul className={pagination}>
                  <li className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}>
                    <Link to="#" className="page-link" onClick={previousPage}><i className="mdi mdi-chevron-left"></i></Link>
                  </li>
                  {(pages[0] > 0) && getPageOptions().slice(0, 1).map((item) => (
                      <li key={item} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active" : ""}`}>
                          <Link to="#" className="page-link" onClick={() => setPageIndex(item)}>{item + 1}</Link>
                      </li>
                  ))}
                  {pages[0] > 1 ? <li key="pagination-dots-start" className="paginate_button page-item"><sub className="fs-4 me-1">...</sub></li> : null}
                  {pages.map((item) => (
                    <li key={item} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active" : ""}`}>
                      <Link to="#" className="page-link" onClick={() => setPageIndex(item)}>{item + 1}</Link>
                    </li>
                  ))}
                  {(pages.length && !hasLastPages && !hasPreLastPage) ? <li key="pagination-dots-end" className="paginate_button page-item"><sub className="fs-4 me-1">...</sub></li> : null}
                  {!hasLastPages && getPageOptions().slice(-1).map((item) => (
                    <li key={item} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active" : ""}`}>
                       <Link to="#" className="page-link" onClick={() => setPageIndex(item)}>{item + 1}</Link>
                    </li>
                  ))}
                  <li className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}>
                    <Link to="#" className="page-link" onClick={nextPage}><i className="mdi mdi-chevron-right"></i></Link>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        )
      }
    </>
  );
};

export default TableContainer;
